import Spinner from '@features/ui-atoms/components/Spinner';
import theme from '@features/ui-layout/styles/theme';
import { Button as ButtonJoy, styled } from '@mui/joy';
import type { SxProps } from '@mui/joy/styles/types';
import { Route } from 'next';
import Link from 'next/link';
import { ReactNode, MouseEvent } from 'react';
import { UrlObject } from 'url';

export enum ButtonVariants {
  PRIMARY,
  SECONDARY,
  TEXT,
  TEXT_PRIMARY,
  MENU,
  LINK_TAB,
  GHOST,
  GHOST_WHITE,
  DESTRUCTIVE,
  DESTRUCTIVE_WHITE,
  PRIMARY_WHITE,
  SECONDARY_WHITE,
}

export interface ButtonProps {
  disabled?: boolean;
  endDecorator?: ReactNode;
  href?: UrlObject | string;
  isLoading?: boolean;
  label?: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  startDecorator?: ReactNode;
  type?: 'button' | 'submit' | 'reset';
  variant?: ButtonVariants;
  sx?: SxProps;
  className?: string;
  iconOnly?: boolean;
}

/**
 * Button component
 * @param param0
 * @param param0.disabled Whether the button is disabled
 * @param param0.endDecorator The decorator to display at the end of the button
 * @param param0.href The URL to navigate to when the button is clicked
 * @param param0.isLoading Whether the button is loading
 * @param param0.label The text to display in the button
 * @param param0.onClick The function to call when the button is clicked
 * @param param0.startDecorator The decorator to display at the start of the button
 * @param param0.type The type of the button
 * @param param0.variant The variant of the button
 * @param param0.iconOnly Whether the button is an icon only button
 * @param param0.sx The style of the button
 */
const Button = ({
  disabled,
  href,
  isLoading,
  label,
  startDecorator,
  type = 'button',
  variant = ButtonVariants.PRIMARY,
  iconOnly,
  ...rest
}: ButtonProps) => {
  const isIconOnly = Boolean(iconOnly || (startDecorator && !label));
  const baseStartDecorator = isLoading ? <Spinner /> : startDecorator;
  const iconOnlyContent = baseStartDecorator ?? label;

  const ButtonComp = (
    <ButtonStyled
      disabled={isLoading || disabled}
      startDecorator={isIconOnly ? undefined : baseStartDecorator}
      data-icon-only={isIconOnly}
      data-variant={variant}
      type={type}
      {...rest}
    >
      {isIconOnly ? iconOnlyContent : label}
    </ButtonStyled>
  );

  if (href) {
    return (
      <Link href={href as Route} passHref>
        {ButtonComp}
      </Link>
    );
  }

  return ButtonComp;
};

export default Button;

type StyleDef = {
  [variant in ButtonVariants]: {
    background: {
      default: string;
      hover: string;
      disabled: string;
    };
    border: string;
    color: {
      default: string;
      hover?: string;
      disabled: string;
    };
  };
};

const styles: StyleDef = {
  [ButtonVariants.PRIMARY]: {
    background: {
      default: theme.palette.bluePrimary,
      hover: theme.palette.blueSecondary,
      disabled: theme.palette.grey3,
    },
    border: 'none',
    color: {
      default: theme.palette.white,
      disabled: theme.palette.white,
    },
  },
  [ButtonVariants.SECONDARY]: {
    background: {
      default: theme.palette.white,
      hover: theme.palette.grey1,
      disabled: theme.palette.white,
    },
    border: `1px solid ${theme.palette.grey2}`,
    color: {
      default: theme.palette.bluePrimary,
      disabled: theme.palette.grey3,
    },
  },
  [ButtonVariants.TEXT]: {
    background: {
      default: 'none',
      hover: 'none',
      disabled: 'none',
    },
    border: 'none',
    color: {
      default: theme.palette.grey4,
      disabled: theme.palette.grey3,
    },
  },
  [ButtonVariants.TEXT_PRIMARY]: {
    background: {
      default: 'none',
      hover: 'none',
      disabled: 'none',
    },
    border: 'none',
    color: {
      default: theme.palette.bluePrimary,
      hover: theme.palette.blueDark,
      disabled: theme.palette.grey3,
    },
  },
  [ButtonVariants.MENU]: {
    background: {
      default: 'transparent',
      hover: theme.palette.blueLight,
      disabled: 'transparent',
    },
    border: 'none',
    color: {
      default: theme.palette.grey5,
      disabled: theme.palette.grey3,
    },
  },
  [ButtonVariants.LINK_TAB]: {
    background: {
      default: 'transparent',
      hover: 'transparent',
      disabled: 'transparent',
    },
    border: 'none',
    color: {
      default: theme.palette.grey5,
      disabled: theme.palette.grey3,
    },
  },
  [ButtonVariants.GHOST]: {
    background: {
      default: theme.palette.white,
      hover: theme.palette.bluePrimary,
      disabled: theme.palette.white,
    },
    border: `1px solid ${theme.palette.bluePrimary}`,
    color: {
      default: theme.palette.bluePrimary,
      hover: theme.palette.white,
      disabled: theme.palette.blueSecondary,
    },
  },
  [ButtonVariants.GHOST_WHITE]: {
    background: {
      default: 'transparent',
      hover: 'transparent',
      disabled: 'transparent',
    },
    border: `1px solid ${theme.palette.white}`,
    color: {
      default: theme.palette.white,
      disabled: theme.palette.white,
    },
  },
  [ButtonVariants.DESTRUCTIVE]: {
    background: {
      default: theme.palette.white,
      hover: theme.palette.orange1,
      disabled: theme.palette.grey3,
    },
    border: `1px solid ${theme.palette.orange1}`,
    color: {
      default: theme.palette.orange1,
      hover: theme.palette.white,
      disabled: theme.palette.white,
    },
  },
  [ButtonVariants.DESTRUCTIVE_WHITE]: {
    background: {
      default: theme.palette.white,
      hover: theme.palette.orange1,
      disabled: theme.palette.grey3,
    },
    border: 'none',
    color: {
      default: theme.palette.orange1,
      hover: theme.palette.white,
      disabled: theme.palette.white,
    },
  },
  [ButtonVariants.PRIMARY_WHITE]: {
    background: {
      default: theme.palette.white,
      hover: theme.palette.white,
      disabled: theme.palette.white,
    },
    border: `1px solid ${theme.palette.white}`,
    color: {
      default: theme.palette.bluePrimary,
      hover: theme.palette.blueSecondary,
      disabled: theme.palette.grey3,
    },
  },
  [ButtonVariants.SECONDARY_WHITE]: {
    background: {
      default: 'transparent',
      hover: theme.palette.white,
      disabled: theme.palette.grey3,
    },
    border: `1px solid ${theme.palette.white}`,
    color: {
      default: theme.palette.white,
      hover: theme.palette.bluePrimary,
      disabled: theme.palette.white,
    },
  },
};

const ButtonStyled = styled(ButtonJoy)(() => ({
  borderRadius: '3px',
  padding: '8px 16px',
  minHeight: 'auto',
  width: 'fit-content',
  fontSize: '14px',
  fontWeight: 500,

  '&[data-icon-only="true"]': {
    padding: '6px !important',
    minWidth: '32px !important',
    width: '32px !important',
    height: '32px !important',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    gap: '0 !important',
    '& > *': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& svg': {
      width: '20px',
      height: '20px',
    },
    '& .MuiCircularProgress-root': {
      width: '20px !important',
      height: '20px !important',
    },
    '& .MuiButton-startDecorator': {
      margin: '0 !important',
      paddingInline: '0 !important',
      paddingBlock: '0 !important',
      '& > svg': {
        width: '20px',
        height: '20px',
      },
    },
  },
  '&[data-variant="0"]': {
    background: styles[ButtonVariants.PRIMARY].background.default,
    color: styles[ButtonVariants.PRIMARY].color.default,
    border: styles[ButtonVariants.PRIMARY].border,
    '&:hover': {
      background: styles[ButtonVariants.PRIMARY].background.hover,
    },
    '&.Mui-disabled': {
      background: styles[ButtonVariants.PRIMARY].background.disabled,
      color: styles[ButtonVariants.PRIMARY].color.disabled,
    },
  },
  '&[data-variant="1"]': {
    background: styles[ButtonVariants.SECONDARY].background.default,
    color: styles[ButtonVariants.SECONDARY].color.default,
    border: styles[ButtonVariants.SECONDARY].border,
    '&:hover': {
      background: styles[ButtonVariants.SECONDARY].background.hover,
    },
    '&.Mui-disabled': {
      background: styles[ButtonVariants.SECONDARY].background.disabled,
      color: styles[ButtonVariants.SECONDARY].color.disabled,
    },
  },
  '&[data-variant="2"]': {
    background: styles[ButtonVariants.TEXT].background.default,
    color: styles[ButtonVariants.TEXT].color.default,
    border: styles[ButtonVariants.TEXT].border,
    padding: '0',
    height: 'fit-content',
    '&:hover': {
      background: styles[ButtonVariants.TEXT].background.hover,
    },
    '&.Mui-disabled': {
      background: styles[ButtonVariants.TEXT].background.disabled,
      color: styles[ButtonVariants.TEXT].color.disabled,
    },
  },
  '&[data-variant="3"]': {
    background: styles[ButtonVariants.TEXT_PRIMARY].background.default,
    color: styles[ButtonVariants.TEXT_PRIMARY].color.default,
    border: styles[ButtonVariants.TEXT_PRIMARY].border,
    padding: '0',
    height: 'fit-content',
    '&:hover': {
      background: styles[ButtonVariants.TEXT_PRIMARY].background.hover,
      color: styles[ButtonVariants.TEXT_PRIMARY].color.hover,
    },
    '&.Mui-disabled': {
      background: styles[ButtonVariants.TEXT_PRIMARY].background.disabled,
      color: styles[ButtonVariants.TEXT_PRIMARY].color.disabled,
    },
  },
  '&[data-variant="4"]': {
    background: styles[ButtonVariants.MENU].background.default,
    color: styles[ButtonVariants.MENU].color.default,
    border: styles[ButtonVariants.MENU].border,
    borderRadius: '4px',
    width: '100%',
    justifyContent: 'flex-start',
    padding: '8px 16px',
    gap: '8px',
    minHeight: '36px',
    fontSize: '16px',
    '& .MuiButton-startDecorator': {
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& > svg': {
        width: '20px',
        height: '20px',
        minWidth: '20px',
        minHeight: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&[data-testid="LockedForumIcon"]': {
          marginLeft: '-4px',
        },
      },
    },
    fontWeight: 400,
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      background: theme.palette.grey1,
      color: theme.palette.grey5,
      '& svg': {
        color: 'inherit',
        '& path': {
          fill: 'currentColor',
        },
      },
    },
    '&.active': {
      fontWeight: 500,
      background: styles[ButtonVariants.MENU].background.hover,
      color: theme.palette.bluePrimary,
      '& svg': {
        color: 'inherit',
        '& path': {
          fill: 'currentColor',
        },
      },
    },
    '&.Mui-disabled': {
      background: styles[ButtonVariants.MENU].background.disabled,
      color: styles[ButtonVariants.MENU].color.disabled,
    },
  },
  '&[data-variant="5"]': {
    background: styles[ButtonVariants.LINK_TAB].background.default,
    color: styles[ButtonVariants.LINK_TAB].color.default,
    border: styles[ButtonVariants.LINK_TAB].border,
    padding: '12px 8px',
    height: 'auto',
    position: 'relative',
    '&:hover': {
      background: 'transparent',
      color: theme.palette.bluePrimary,
    },
    '&[data-selected="true"]': {
      color: theme.palette.bluePrimary,
      fontWeight: 700,
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '2px',
        backgroundColor: theme.palette.bluePrimary,
      },
    },
  },
  '&[data-variant="6"]': {
    background: styles[ButtonVariants.GHOST].background.default,
    color: styles[ButtonVariants.GHOST].color.default,
    border: styles[ButtonVariants.GHOST].border,
    transition: 'background 0.3s ease',
    '&:hover': {
      background: styles[ButtonVariants.GHOST].background.hover,
      color: styles[ButtonVariants.GHOST].color.hover,
    },
  },
  '&[data-variant="7"]': {
    background: styles[ButtonVariants.GHOST_WHITE].background.default,
    color: styles[ButtonVariants.GHOST_WHITE].color.default,
    border: styles[ButtonVariants.GHOST_WHITE].border,
  },
  '& .MuiButton-startDecorator': {
    marginRight: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& > svg': {
      width: '20px',
      height: '20px',
      minWidth: '20px',
      minHeight: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  '&.Mui-disabled': {
    cursor: 'not-allowed',
    pointerEvents: 'all !important',
  },
  '&[data-variant="8"]': {
    background: styles[ButtonVariants.DESTRUCTIVE].background.default,
    color: styles[ButtonVariants.DESTRUCTIVE].color.default,
    border: styles[ButtonVariants.DESTRUCTIVE].border,
    '&:hover': {
      background: styles[ButtonVariants.DESTRUCTIVE].background.hover,
      color: styles[ButtonVariants.DESTRUCTIVE].color.hover,
    },
    '&.Mui-disabled': {
      background: styles[ButtonVariants.DESTRUCTIVE].background.disabled,
      color: styles[ButtonVariants.DESTRUCTIVE].color.disabled,
    },
  },
  '&[data-variant="9"]': {
    padding: '9px 17px',
    background: styles[ButtonVariants.DESTRUCTIVE_WHITE].background.default,
    color: styles[ButtonVariants.DESTRUCTIVE_WHITE].color.default,
    border: styles[ButtonVariants.DESTRUCTIVE_WHITE].border,
    '&:hover': {
      background: styles[ButtonVariants.DESTRUCTIVE_WHITE].background.hover,
      color: styles[ButtonVariants.DESTRUCTIVE_WHITE].color.hover,
    },
    '&.Mui-disabled': {
      background: styles[ButtonVariants.DESTRUCTIVE_WHITE].background.disabled,
      color: styles[ButtonVariants.DESTRUCTIVE_WHITE].color.disabled,
    },
  },
  '&[data-variant="10"]': {
    background: styles[ButtonVariants.PRIMARY_WHITE].background.default,
    color: styles[ButtonVariants.PRIMARY_WHITE].color.default,
    border: styles[ButtonVariants.PRIMARY_WHITE].border,
    '&:hover': {
      background: styles[ButtonVariants.PRIMARY_WHITE].background.hover,
      color: styles[ButtonVariants.PRIMARY_WHITE].color.hover,
    },
    '&.Mui-disabled': {
      background: styles[ButtonVariants.PRIMARY_WHITE].background.disabled,
      color: styles[ButtonVariants.PRIMARY_WHITE].color.disabled,
    },
  },
  '&[data-variant="11"]': {
    background: styles[ButtonVariants.SECONDARY_WHITE].background.default,
    color: styles[ButtonVariants.SECONDARY_WHITE].color.default,
    border: styles[ButtonVariants.SECONDARY_WHITE].border,
    '&:hover': {
      background: styles[ButtonVariants.SECONDARY_WHITE].background.hover,
      color: styles[ButtonVariants.SECONDARY_WHITE].color.hover,
    },
    '&.Mui-disabled': {
      background: styles[ButtonVariants.SECONDARY_WHITE].background.disabled,
      color: styles[ButtonVariants.SECONDARY_WHITE].color.disabled,
    },
  },
}));
